import { icbtnHtml, motionOk } from "./kit";
import { uiUrl } from "./portraits";

export interface TitleHandlers { onStart: () => void; onSettings: () => void }

/** What the title needs of its live diorama (render/titleDiorama.ts): frames drawn, the camera lean, the world jolt. */
export interface TitleLive {
  readonly frames: number;
  lean: { x: number; y: number };
  thump(amount?: number): void;
}

/** The boot's tips on the loading plate (the loading card had them; they are true from the very first launch). */
const BOOT_TIPS = [
  "Every road card lengthens the march. More road, more time under fire.",
  "Build on the bends: a tower there covers more road.",
  "Upgrade cards raise a tower a tier. The fortress too.",
  "The boss marches with the last wave. Hold an upgrade back for it.",
  "Chests hold gear. Arm your towers between battles.",
];

/** The showcase still: the live title's opening frame (render/titleDiorama.ts SHOWCASE, tools/video/title_still.mjs). */
export const TITLE_STILL = "title/showcase.jpg";

/**
 * Boot splash and title in one screen (owner, 2026-10-06: "the dimensions change between these two"). From the
 * launch image on it is one picture: the showcase still (the live title's own opening frame, which the iOS launch
 * images are cut from too) with the logo at the one size and place it always has, and where TAP TO START will be, the
 * loading plate (LOADING n/m and a tip). Nothing moves the logo.
 *
 * While the files come in, the live diorama (render/titleDiorama.ts) is built behind the still as soon as its own
 * models are in, and once it has drawn its first frames the still cross-fades into it (`goLive`) — usually before
 * the load ends — and the logo lands (a lift and a slam, a shine, dust, a jolt of the world). When the load is done
 * (`ready`) the plate turns into TAP TO START and the screen takes taps.
 *
 * Without the diorama (low quality tier, Reduced effects, reduced motion) the still itself drifts (a slow push-in
 * and a warm glint) and the logo lands on it once the title is ready. Reduced motion: the still, unmoving. The live
 * camera leans with the device (or the pointer on desktop). The first tap is also the audio unlock gesture.
 */
export class TitleScreen {
  /** Without a live scene the logo lands this long after the title is ready. */
  static readonly LandWithoutLiveMs = 400;
  /** A live scene still being built when the title is ready: the logo waits this long for it before landing. */
  static readonly WaitForLiveMs = 2500;
  /** When, into the landing animation, the logo hits (the shake and the dust go off). */
  static readonly SlamAtMs = 400;
  /** The still's cross-fade into the live scene. */
  static readonly LiveFadeMs = 1100;
  /** How long each boot tip stays. */
  static readonly TipMs = 3200;

  readonly root: HTMLDivElement;
  private raf = 0;
  private tiltX = 0;
  private tiltY = 0;
  private live: TitleLive | null = null;
  private landed = false;
  private isReady = false;
  /** The loading bar's furthest share (M49: it never runs backwards). */
  private shownProgress = 0;
  private tip = 0;
  private readonly timers: ReturnType<typeof setTimeout>[] = [];
  private readonly tipTimer: ReturnType<typeof setInterval>;
  private readonly onTilt = (e: DeviceOrientationEvent) => { this.tiltX = Math.max(-1, Math.min(1, (e.gamma ?? 0) / 25)); this.tiltY = Math.max(-1, Math.min(1, ((e.beta ?? 45) - 45) / 25)); };
  private readonly onMove = (e: PointerEvent) => { this.tiltX = (e.clientX / innerWidth) * 2 - 1; this.tiltY = (e.clientY / innerHeight) * 2 - 1; };

  /** `expectLive`: a diorama is on its way, so the still holds perfectly still (it must match the live first frame). */
  constructor(parent: HTMLElement, private readonly version: string, private readonly h: TitleHandlers, private readonly expectLive: boolean) {
    this.root = document.createElement("div");
    // `.title` carries the dusk gradient, so a slow decode or a missing file shows the right colours
    this.root.className = `screen title booting${motionOk() ? "" : " still"}${expectLive ? "" : " nolive"}`;
    document.body.classList.add("at-title");                       // hides the perf overlay behind the still
    const logo = uiUrl("title/logo.png");
    this.root.innerHTML = `
      <div class="stage">
        <img class="keyart" decoding="async" src="${uiUrl(TITLE_STILL)}" alt="">
        <div class="glint"></div>
      </div>
      <div class="shade"></div>
      <div class="lockup">
        <div class="burst"></div>
        <img class="logo" decoding="sync" src="${logo}" alt="Highmarch">
        <div class="shine" style="-webkit-mask-image:url('${logo}');mask-image:url('${logo}')"><i></i></div>
        <div class="dust">${"<i></i>".repeat(7)}</div>
      </div>
      <div class="bootplate"><div class="lbl disp"><span>LOADING</span><span class="count"></span></div><div class="track"><i></i></div></div>
      <p class="boottip"></p>
      <div class="version">${version}</div>`;
    parent.append(this.root);
    // Straight from the launch image / the page's own splash the still is already decoded: it is up in the very
    // first frame, with no fade through the gradient.
    const art0 = this.root.querySelector<HTMLImageElement>(".keyart")!;
    if (art0.complete && art0.naturalWidth > 0) art0.classList.add("ok");
    void this.reveal();
    this.root.addEventListener("pointerup", (e) => { if (this.isReady && !(e.target as HTMLElement).closest(".settings")) this.h.onStart(); });
    window.addEventListener("deviceorientation", this.onTilt);
    window.addEventListener("pointermove", this.onMove);
    this.root.querySelector(".boottip")!.textContent = BOOT_TIPS[0];
    this.tipTimer = setInterval(() => this.nextTip(), TitleScreen.TipMs);
    let last = performance.now(), leanX = 0, leanY = 0;
    const step = (t: number) => {
      const dt = Math.min(0.1, (t - last) / 1000);
      last = t;
      // the lean eases toward the tilt: a raw gyro reading jitters
      leanX += (this.tiltX - leanX) * Math.min(1, dt * 4); leanY += (this.tiltY - leanY) * Math.min(1, dt * 4);
      if (this.live) { this.live.lean.x = leanX; this.live.lean.y = leanY; }
      this.raf = requestAnimationFrame(step);
    };
    this.raf = requestAnimationFrame(step);
  }

  /** The boot's progress (files in, of how many), on the loading plate. */
  progress(done: number, total: number): void {
    if (this.isReady) return;
    // never backwards (M49): a later phase may report against a bigger total
    const f = total > 0 ? Math.min(1, done / total) : 0;
    if (f < this.shownProgress) return;
    this.shownProgress = f;
    this.root.querySelector(".bootplate .count")!.textContent = total > 0 ? `${done}/${total}` : "";
    this.root.querySelector<HTMLElement>(".bootplate .track i")!.style.transform = `scaleX(${total > 0 ? Math.min(1, done / total) : 0})`;
  }

  /**
   * The load is done: the plate turns into TAP TO START (the ribbon is only added now, so nothing can tap or wait on
   * it early), the settings button comes in and taps start the game. Without a live scene, the logo lands now.
   */
  ready(): void {
    if (this.isReady || !this.root.isConnected) return;
    this.isReady = true;
    clearInterval(this.tipTimer);
    this.root.querySelector<HTMLElement>(".bootplate .track i")!.style.transform = "scaleX(1)";
    this.root.insertAdjacentHTML("beforeend", `<div class="ribbon disp tap">TAP TO START</div>${icbtnHtml("gear", "settings", "", null, 44)}`);
    this.root.querySelector<HTMLButtonElement>(".settings")!.onclick = (e) => { e.stopPropagation(); this.h.onSettings(); };
    this.root.classList.remove("booting");
    this.root.classList.add("ready");
    // the logo lands on the live world when it comes (goLive); one still on its way gets a moment before it lands anyway
    if (!this.live) this.timers.push(setTimeout(() => this.land(), this.expectLive ? TitleScreen.WaitForLiveMs : TitleScreen.LandWithoutLiveMs));
  }

  /**
   * The live scene has drawn its first frames behind the still: the still fades into it, and the logo lands on the
   * live world (unless it already has).
   */
  goLive(live: TitleLive): void {
    if (!this.root.isConnected || this.live) return;
    this.live = live;
    this.root.classList.add("live");
    // the gradient behind the still would show through the fade instead of the world: the canvas is under the title
    requestAnimationFrame(() => this.root.classList.add("fading"));
    this.timers.push(setTimeout(() => this.root.classList.add("on-live"), TitleScreen.LiveFadeMs));
    if (!this.landed) this.timers.push(setTimeout(() => this.land(), TitleScreen.LiveFadeMs * 0.55));
  }

  /** The logo lands: lift, slam, a shine across it, dust, and the ground shakes (the live world, or the still). */
  private land(): void {
    if (this.landed || !this.root.isConnected) return;
    this.landed = true;
    if (!motionOk()) return;
    this.root.classList.add("landing");
    this.timers.push(setTimeout(() => {
      if (!this.root.isConnected) return;
      this.root.classList.add("slam");
      if (this.live) this.live.thump(); else this.root.classList.add("quake");
    }, TitleScreen.SlamAtMs));
  }

  private nextTip(): void {
    const el = this.root.querySelector<HTMLElement>(".boottip");
    if (!el) return;
    this.tip = (this.tip + 1) % BOOT_TIPS.length;
    el.classList.remove("tip-in");
    void el.offsetWidth;                                      // restarts the fade-in
    el.textContent = BOOT_TIPS[this.tip];
    el.classList.add("tip-in");
  }

  /**
   * `img.decode()` resolves once the bitmap is ready to paint. Capped at 1.5 s — a stalled image never holds the
   * screen back. The still has its own opacity (`.keyart.ok`) so a failed load never shows a broken-image box. Once
   * it is up, the page's own splash under it (index.html #boot-splash, there from the first paint) goes.
   */
  private async reveal(): Promise<void> {
    const art = this.root.querySelector<HTMLImageElement>(".keyart")!;
    const artReady = () => art.complete && art.naturalWidth > 0;
    const decodeArt = typeof art.decode === "function" ? art.decode().catch(() => undefined) : Promise.resolve();
    await Promise.race([decodeArt, new Promise((res) => setTimeout(res, 1500))]);
    if (!this.root.isConnected) return;
    if (artReady()) art.classList.add("ok");
    else void decodeArt.then(() => { if (artReady()) art.classList.add("ok"); });
    requestAnimationFrame(() => { removeBootSplash(); this.root.classList.add("in"); });
  }

  dispose(): void {
    document.body.classList.remove("at-title");
    cancelAnimationFrame(this.raf);
    clearInterval(this.tipTimer);
    for (const t of this.timers.splice(0)) clearTimeout(t);
    window.removeEventListener("deviceorientation", this.onTilt);
    window.removeEventListener("pointermove", this.onMove);
    this.live = null;
    removeBootSplash();
    this.root.remove();
  }
}

/** The page's own first-paint splash (index.html): the still and the logo before any script has run. */
export function removeBootSplash(): void { document.getElementById("boot-splash")?.remove(); }
